import { Component, DoCheck, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ID } from '@datorama/akita';
import { NgForm } from '@angular/forms';

import { Form, FormSection, SectionType } from '@state/form';
import { FormValues, FormWizardQuery, FormWizardStore, NextEvent, SubmitEvent } from '@state/form-wizard';
import { MessageBoxType } from '@component/message-box';
import { SignatureRequestQuery } from '@state/signature-request';

import { RenderedFormService } from '../rendered-form.service';
import { SectionComponent } from '../section/section.component';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-form-wizard',
  styleUrls: ['./form-wizard.component.scss'],
  templateUrl: './form-wizard.component.html'
})
export class FormWizardComponent implements DoCheck, OnInit {
  @Input() form: Form;
  @Input() signatureRequest: any;
  @Input() vehicle?: { entity_id: ID; id: ID };

  @Output() flBack: EventEmitter<SectionComponent> = new EventEmitter();
  @Output() flNext: EventEmitter<NextEvent> = new EventEmitter();
  @Output() flSubmit: EventEmitter<SubmitEvent> = new EventEmitter();

  activeIndex$ = this.formWizardQuery.activeIndex$;
  activeSection$ = this.formWizardQuery.activeSection$;
  showWarningOnUpdate$ = this.formWizardQuery.showWarningOnUpdate$;
  isSubmitting$ = this.formWizardQuery.isSubmitting$;
  signatureRequestSubmitting$ = this.signatureRequestQuery.submitting$;

  currentSection: SectionComponent;
  formValues: FormValues;
  isCurrentSectionInvalid: boolean;
  messageBoxType = MessageBoxType;
  sectionForms: NgForm[] = [];

  get visibleSections(): FormSection[] {
    let { form, renderedFormService } = this;
    return form.sections.filter((s) => renderedFormService.objectIsVisible(s));
  }

  readonly SectionType = SectionType;

  constructor(
    private formWizardQuery: FormWizardQuery,
    private formWizardStore: FormWizardStore,
    private signatureRequestQuery: SignatureRequestQuery,
    public renderedFormService: RenderedFormService
  ) {}

  ngOnInit() {
    this.formValues = { ...this.renderedFormService.dynamicModels };
  }

  ngDoCheck(): void {
    if (this.currentSection) this.updateCurrentSectionValidity(this.currentSection);
  }

  back(currentSection?: SectionComponent): void {
    this.renderedFormService.dynamicModels = { ...this.formValues };
    this.currentSection = null;
    this.isCurrentSectionInvalid = false;
    this.flBack.emit(currentSection);
  }

  ctaLabel = (index: number, section: any): string => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    if (section.cta_label) return section.cta_label;
    if (this.isLastSection(index)) return 'Submit';

    return 'Save and Continue';
  };

  isLastSection = (index: number): boolean => index === this.visibleSections.length - 1;

  determineButtonType = (sectionIndex: number): string =>
    this.isLastSection(sectionIndex) ? 'submit' : 'button';

  doNothing(): boolean {
    return false;
  }

  highlightInvalidFields(): void {
    for (let controlKey of Object.keys(this.currentSection.sectionForm.controls)) {
      this.currentSection.sectionForm.controls[controlKey].markAsTouched();
    }
  }

  generateFormDiff(originalFormValue: FormValues, currentSectionFormValues: FormValues): FormValues {
    let diffObject = {};
    for (let key in originalFormValue) {
      if (originalFormValue[key] !== currentSectionFormValues[key]) {
        diffObject[key] = currentSectionFormValues[key];
      }
    }
    return diffObject;
  }

  next(sectionIndex: number, currentSection: SectionComponent, event: any, forceSubmit = false): void {
    let { showWarningOnUpdate } = this.formWizardQuery.getValue();
    if (!showWarningOnUpdate) this.formWizardStore.update({ showWarningOnUpdate: true });
    if (currentSection) this.updateCurrentSectionValidity(currentSection);
    if (this.isCurrentSectionInvalid) {
      this.highlightInvalidFields();
      return;
    }
    let [sectionForm] = this.sectionForms;
    let updatedFormValues = {};
    let currentFormValues: FormValues;
    let activeSectionFormValues: FormValues;

    if (event && event.initialForm) {
      activeSectionFormValues = event.initialForm;
      updatedFormValues = event.initialForm;
    }

    if (sectionForm) {
      currentFormValues = this.renderedFormService.dynamicModels;
      let keys = Object.keys(currentFormValues);
      for (let key of keys) {
        let formValue = currentFormValues[key];
        if (typeof formValue === 'string') {
          if (formValue.charAt(0) === '$') {
            formValue = parseFloat(formValue.replace(/\$|,/g, ''));
            currentFormValues[key] = formValue;
          }
        }
      }
      if (event && event.initialForm) this.formValues = event.initialForm;
      if (!Object.entries(this.formValues).length) {
        let formKeys = Object.keys(currentFormValues);
        for (let key of formKeys) {
          this.formValues[key] = undefined;
        }
      }
      updatedFormValues = this.generateFormDiff(this.formValues, currentFormValues);
      activeSectionFormValues = sectionForm.form.value;
    }

    if (this.isLastSection(sectionIndex) || forceSubmit) {
      if (activeSectionFormValues) {
        this.submit(activeSectionFormValues);
        return;
      }
      this.submit();
    } else {
      this.formValues = { ...currentFormValues };
      // Note: $event that bubbles up from here to task/data-request component
      // { showProgress: boolean } is only used in the task.component and is not needed for data-requests
      let params: NextEvent = {
        ...event,
        activeSectionFormValues,
        currentSection,
        sectionIndex,
        updatedFormValues
      };
      this.flNext.emit(params);
    }
    this.formWizardStore.update({ showWarningOnUpdate: false });
  }

  submit(activeSectionFormValues?: FormValues): void {
    let currentFormValues: FormValues = this.renderedFormService.dynamicModels;
    let updatedFormValues = this.generateFormDiff(this.formValues, currentFormValues);
    this.flSubmit.emit({ activeSectionFormValues, updatedFormValues });
  }

  submitPreview(event: any): void {
    if (event.dynamicModels) confirm('You have reached the end of the form');
  }

  updateCurrentSectionValidity(currentSection: SectionComponent): void {
    this.currentSection = currentSection;
    this.isCurrentSectionInvalid = !currentSection.valid;
  }
}
